import { type ComponentProps, type ReactNode, useId } from "react"
import { cn } from "./lib"

/** Native input attributes retain form submission, grouping, and keyboard behavior. */
export type RadioCardProps = Omit<
	ComponentProps<"input">,
	"type" | "className" | "title" | "children"
> & {
	title: string
	description?: ReactNode
	icon?: ReactNode
	/** Extends the visible card while keeping the native input's full-card hit target. */
	className?: string
}

/** A descriptive radio choice; presentation follows shared tokens and caller-provided content. */
export function RadioCard({
	title,
	description,
	icon,
	className,
	id,
	checked,
	"aria-label": ariaLabel = title,
	"aria-describedby": describedBy,
	...inputProps
}: RadioCardProps) {
	const generatedId = useId()
	const inputId = id ?? generatedId
	const descriptionId = `${generatedId}-description`
	const hasDescription = description !== undefined && description !== null

	return (
		<label
			htmlFor={inputId}
			className={cn(
				"group relative grid min-h-20 min-w-0 cursor-pointer items-start gap-[9px] rounded-[10px] border border-mm-border bg-mm-background p-[11px] hover:border-[var(--radio-card-hover-border,var(--mm-border-strong))] focus-within:outline-[3px] focus-within:outline-offset-2 focus-within:outline-mm-info/25 has-[:checked]:border-mm-info has-[:checked]:bg-mm-info-soft has-[:checked]:shadow-[0_0_0_1px_var(--mm-info)] has-[:disabled]:cursor-default has-[:disabled]:opacity-55",
				icon
					? "grid-cols-[30px_minmax(0,1fr)_14px]"
					: "grid-cols-[minmax(0,1fr)_14px]",
				className,
			)}
			data-selected={checked}
		>
			{icon && (
				<span
					className="grid size-[30px] place-items-center rounded-[8px] bg-mm-muted text-[var(--ui-marker-foreground,var(--mm-muted-fg))] group-has-[:checked]:bg-mm-background group-has-[:checked]:text-mm-info [&_svg]:size-3.5"
					aria-hidden
				>
					{icon}
				</span>
			)}
			<span>
				<strong className="block text-xs font-bold leading-[1.45]">
					{title}
				</strong>
				{hasDescription && (
					<small
						id={descriptionId}
						className="mt-[3px] block text-[10px] leading-[1.35] text-mm-muted-fg"
					>
						{description}
					</small>
				)}
			</span>
			{/* CSS reads the native checked/disabled state, including uncontrolled inputs and disabled fieldsets. */}
			<input
				{...inputProps}
				id={inputId}
				className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-default"
				type="radio"
				aria-label={ariaLabel}
				aria-describedby={
					[describedBy, hasDescription ? descriptionId : undefined]
						.filter(Boolean)
						.join(" ") || undefined
				}
				checked={checked}
			/>
			<span
				className="mt-0.5 size-[13px] rounded-full border border-[var(--radio-card-indicator-border,var(--mm-border-strong))] shadow-[inset_0_0_0_3px_var(--mm-background)] group-has-[:checked]:border-mm-info group-has-[:checked]:bg-mm-info"
				aria-hidden
			/>
		</label>
	)
}
